import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { Package, Clock, CheckCircle2, Send, AlertTriangle, TrendingUp, RotateCw, RefreshCw, PackageCheck, UserCheck, BarChart3, ArrowUpRight, ArrowDownRight, Minus, Trophy, Download, FileText } from "lucide-react";
import { orderItems } from "../lib/constants";
import { downloadCSV } from "../lib/csv";

const CARDS = [
  { key: "created_today", label: "Bugün Oluşturulan", icon: TrendingUp, cls: "from-blue-500/20 to-blue-500/5 text-blue-600", border: "border-blue-500/30" },
  { key: "active", label: "Aktif (Yeni + Hazırlanıyor)", icon: Clock, cls: "from-amber-500/20 to-amber-500/5 text-amber-600", border: "border-amber-500/30" },
  { key: "ready_waiting", label: "Hazır (Bekleyen)", icon: Package, cls: "from-emerald-500/20 to-emerald-500/5 text-emerald-600", border: "border-emerald-500/30" },
  { key: "notified_today", label: "Bugün Bildirilen", icon: Send, cls: "from-cyan-500/20 to-cyan-500/5 text-cyan-600", border: "border-cyan-500/30" },
  { key: "delivered_total", label: "Teslim Edilen", icon: CheckCircle2, cls: "from-slate-500/20 to-slate-500/5 text-slate-600", border: "border-slate-500/30" },
  { key: "whatsapp_failed", label: "Başarısız Bildirim", icon: AlertTriangle, cls: "from-rose-500/20 to-rose-500/5 text-rose-600", border: "border-rose-500/30" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("week");
  const [report, setReport] = useState(null);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const reportQuery = useCallback(() => {
    if (period === "custom" && rangeFrom && rangeTo) return `period=custom&start=${rangeFrom}&end=${rangeTo}`;
    return `period=${period}`;
  }, [period, rangeFrom, rangeTo]);

  useEffect(() => {
    if (period === "custom" && (!rangeFrom || !rangeTo)) return;
    let active = true;
    api.get(`/reports/summary?${reportQuery()}`)
      .then(({ data: d }) => { if (active) setReport(d); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
    return () => { active = false; };
  }, [period, rangeFrom, rangeTo, reportQuery]);

  const exportReportCSV = () => {
    if (!report) return;
    const q = (v) => { const s = v == null ? "" : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const L = [];
    L.push(`Özet Rapor;${report.period_label} (${report.start_date} - ${report.end_date})`);
    L.push(`Oluşturulan;${report.totals.created}`);
    L.push(`Teslim Edilen;${report.totals.delivered}`);
    L.push(`İptal;${report.totals.cancelled}`);
    L.push("");
    L.push("En Çok Sipariş Veren Müşteriler");
    L.push("#;Firma;Sipariş");
    report.top_customers.forEach((c, i) => L.push(`${i + 1};${q(c.company_name)};${c.orders}`));
    L.push("");
    L.push("En Çok Sipariş Edilen Ürünler");
    L.push("#;Kod;Ürün;Sipariş;Toplam Miktar");
    report.top_products.forEach((p, i) => L.push(`${i + 1};${q(p.product_code)};${q(p.product_name)};${p.orders};${p.qty}`));
    downloadCSV("\uFEFF" + L.join("\r\n"), `ozet_rapor_${report.period}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportReportPDF = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    window.open(`${base}/api/reports/summary.pdf?${reportQuery()}&token=${encodeURIComponent(token || "")}`, "_blank");
  };

  const resend = async (id) => {
    try {
      const { data: o } = await api.post(`/orders/${id}/resend-whatsapp`);
      if (o.whatsapp_status === "success") toast.success(`#${o.order_no} E-posta gönderildi ✓`);
      else toast.error(`#${o.order_no} tekrar başarısız — personele uyarı iletildi`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Yönetim Paneli</h1>
          <p className="text-sm text-muted-foreground">Günlük özet · otomatik yenilenir</p>
        </div>
        <button data-testid="dashboard-refresh" onClick={load} className="ml-auto flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm hover:bg-secondary">
          <RefreshCw className="h-4 w-4" /> Yenile
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="dashboard-cards">
        {CARDS.map((c) => (
          <div key={c.key} data-testid={`dash-card-${c.key}`} className={`rounded-2xl border bg-gradient-to-br ${c.cls} ${c.border} p-5`}>
            <div className="flex items-center justify-between">
              <c.icon className="h-6 w-6" />
              <span className="font-mono text-4xl font-black text-foreground">{data ? data[c.key] : "–"}</span>
            </div>
            <div className="mt-3 text-sm font-semibold text-foreground/80">{c.label}</div>
          </div>
        ))}
      </div>

      {data?.daily && (() => {
        const max = Math.max(1, ...data.daily.flatMap((x) => [x.created, x.delivered]));
        return (
          <div className="mt-8" data-testid="dashboard-trends">
            <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold">
              <BarChart3 className="h-5 w-5 text-blue-500" /> Haftalık Trend (Son 7 Gün)
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-2xl border border-border bg-white p-5 lg:col-span-2">
                <div className="mb-4 flex items-center gap-4 text-xs font-semibold">
                  <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-blue-500" /> Oluşturulan</span>
                  <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-500" /> Teslim Edilen</span>
                </div>
                <div className="flex items-end justify-between gap-2">
                  {data.daily.map((d) => (
                    <div key={d.date} className="flex flex-1 flex-col items-center gap-1.5" data-testid={`trend-day-${d.date}`}>
                      <div className="flex h-[140px] w-full items-end justify-center gap-1">
                        <div className="w-1/2 max-w-[22px] rounded-t bg-blue-500 transition-all" style={{ height: `${Math.max(d.created ? 4 : 0, (d.created / max) * 100)}%` }} title={`${d.created} oluşturuldu`} />
                        <div className="w-1/2 max-w-[22px] rounded-t bg-emerald-500 transition-all" style={{ height: `${Math.max(d.delivered ? 4 : 0, (d.delivered / max) * 100)}%` }} title={`${d.delivered} teslim`} />
                      </div>
                      <div className="text-xs font-semibold text-muted-foreground">{d.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-rows-2 gap-4">
                <TrendCard testid="trend-card-created" label="Bu Hafta Oluşturulan" value={data.week?.created} prev={data.prev_week?.created} color="text-blue-600" />
                <TrendCard testid="trend-card-delivered" label="Bu Hafta Teslim" value={data.week?.delivered} prev={data.prev_week?.delivered} color="text-emerald-600" />
              </div>
            </div>
          </div>
        );
      })()}

      {report && (
        <div className="mt-8" data-testid="dashboard-reports">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold">
              <Trophy className="h-5 w-5 text-amber-500" /> En Aktif Müşteri & Ürünler
            </h2>
            <div className="flex rounded-xl border border-border bg-white p-1">
              {[["week", "Haftalık"], ["month", "Aylık"], ["custom", "Özel"]].map(([k, l]) => (
                <button key={k} data-testid={`report-period-${k}`} onClick={() => setPeriod(k)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${period === k ? "bg-primary text-white" : "text-muted-foreground hover:bg-secondary"}`}>
                  {l}
                </button>
              ))}
            </div>
            {period === "custom" && (
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="report-range-from" type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)}
                  className="rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm outline-none focus:border-primary" />
                <span className="text-sm text-muted-foreground">–</span>
                <input data-testid="report-range-to" type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)}
                  className="rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm outline-none focus:border-primary" />
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <button data-testid="report-export-csv" onClick={exportReportCSV}
                className="flex items-center gap-1.5 rounded-xl border border-border bg-white px-3 py-2 text-sm font-semibold hover:bg-secondary">
                <Download className="h-4 w-4" /> CSV
              </button>
              <button data-testid="report-export-pdf" onClick={exportReportPDF}
                className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white hover:brightness-110">
                <FileText className="h-4 w-4" /> PDF
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-white p-5" data-testid="report-top-customers">
              <div className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">En Çok Sipariş Veren Müşteriler</div>
              {report.top_customers.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Bu dönemde kayıt yok.</div>}
              <div className="space-y-2">
                {report.top_customers.map((c, i) => (
                  <div key={c.company_name} data-testid={`report-customer-${i}`} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-black text-amber-600">{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate font-semibold">{c.company_name}</span>
                    <span className="font-mono text-sm font-bold text-primary">{c.orders} sipariş</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-white p-5" data-testid="report-top-products">
              <div className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">En Çok Sipariş Edilen Ürünler</div>
              {report.top_products.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Bu dönemde kayıt yok.</div>}
              <div className="space-y-2">
                {report.top_products.map((p, i) => (
                  <div key={p.product_code} data-testid={`report-product-${i}`} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-black text-emerald-600">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold"><span className="font-mono text-primary">{p.product_code}</span> {p.product_name}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-mono text-sm font-bold text-primary">{p.orders} sipariş</div>
                      <div className="text-xs text-muted-foreground">{p.qty} toplam</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold">
          <AlertTriangle className="h-5 w-5 text-rose-500" /> Başarısız E-posta Bildirimleri
        </h2>
        <div className="space-y-2" data-testid="dashboard-failed-list">
          {(!data || data.failed_orders?.length === 0) && (
            <div className="rounded-xl border border-dashed border-border py-10 text-center text-muted-foreground">
              Başarısız bildirim yok. 🎉
            </div>
          )}
          {data?.failed_orders?.map((o) => (
            <div key={o.id} data-testid={`dash-failed-${o.order_no}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-primary">{o.order_no}</span>
                  <StatusBadge status={o.status} />
                </div>
                <div className="mt-1 text-sm font-semibold">{o.customer_snapshot?.company_name}</div>
                <div className="text-xs text-muted-foreground">
                  {orderItems(o).map((it) => `${it.product_code} ${it.quantity}${it.unit}`).join(", ")} · {o.whatsapp_last_error}
                </div>
              </div>
              <button
                data-testid={`dash-resend-${o.order_no}`}
                onClick={() => resend(o.id)}
                className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/30 hover:brightness-110"
              >
                <RotateCw className="h-4 w-4" /> Yeniden Gönder
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold">
          <PackageCheck className="h-5 w-5 text-cyan-500" /> Son Teslimatlar
        </h2>
        <div className="space-y-2" data-testid="dashboard-deliveries-list">
          {(!data || data.recent_deliveries?.length === 0) && (
            <div className="rounded-xl border border-dashed border-border py-10 text-center text-muted-foreground">
              Henüz teslimat yok.
            </div>
          )}
          {data?.recent_deliveries?.map((o) => (
            <div key={o.id} data-testid={`dash-delivery-${o.order_no}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-3.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-primary">{o.order_no}</span>
                  <span className="text-sm font-semibold">{o.customer_snapshot?.company_name}</span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {orderItems(o).map((it) => `${it.product_code} ${it.quantity}${it.unit}`).join(", ")}
                </div>
              </div>
              <div className="text-right text-xs">
                <div className="flex items-center justify-end gap-1 font-semibold text-cyan-700"><Clock className="h-3.5 w-3.5" />{o.delivered_at ? new Date(o.delivered_at).toLocaleString("tr-TR") : "-"}</div>
                {o.received_by && <div className="flex items-center justify-end gap-1 text-muted-foreground"><UserCheck className="h-3.5 w-3.5" />{o.received_by}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrendCard({ testid, label, value = 0, prev = 0, color }) {
  const diff = value - prev;
  const pct = prev > 0 ? Math.round((diff / prev) * 100) : value > 0 ? 100 : 0;
  const flat = diff === 0;
  const up = diff > 0;
  return (
    <div className="flex flex-col justify-center rounded-2xl border border-border bg-white p-5" data-testid={testid}>
      <div className="text-sm font-semibold text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono text-4xl font-black ${color}`}>{value}</div>
      <div className={`mt-2 inline-flex items-center gap-1 text-xs font-bold ${flat ? "text-slate-400" : up ? "text-emerald-600" : "text-rose-500"}`}>
        {flat ? <Minus className="h-3.5 w-3.5" /> : up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
        {flat ? "Önceki haftayla aynı" : `${up ? "+" : ""}${pct}% · önceki hafta ${prev}`}
      </div>
    </div>
  );
}
